import Microgame from '../engine/Microgame.js';

export default class DodgeVerticalGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Chute !`, en: `Fall!`, es: `¡Caída!` },
            controls: { fr: `Glisse de gauche à droite pendant ta chute (ou clique)`, en: `Swipe left/right while falling (or click)`, es: `Desliza a los lados mientras caes (o haz clic)` },
            objective: { fr: `Survis à la chute libre en évitant les obstacles rouges`, en: `Survive the free fall and avoid red obstacles`, es: `Sobrevive a la caída libre y evita los obstáculos rojos` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="m5 16 7 7 7-7"/><path d="M12 2v21"/><path d="M5 8h14"/><path d="M3 3h18"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 20, duration: 200 },
                { type: 'swipe', fromX: 50, fromY: 20, toX: 30, toY: 20, duration: 260 },
                { type: 'wait', duration: 240 },
                { type: 'swipe', fromX: 30, fromY: 20, toX: 70, toY: 20, duration: 260 },
                { type: 'wait', duration: 240 },
                { type: 'swipe', fromX: 70, fromY: 20, toX: 45, toY: 20, duration: 260 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.player = null;
        this.obstacles = [];
        this.playerX = 0;

        const sm = engine.speedMultiplier || 1.0;
        const speedFactor = 1 + (sm - 1) * (1.7 / 3); // Max logic 4.0 becomes 2.7
        this.speed = 300 * speedFactor; // Fall speed
        this.obstacleSpeed = 300 * speedFactor; // Upward speed (relative)
        this.spawnTimer = 0;
        this.spawnInterval = 0.65 / speedFactor; // Increased base spacing from 0.5 to 0.65
        this.isDragging = false;
        this.lastDragX = null;
    }

    setup() {
        this.engine.ui.instruction.textContent = "DIVE!";

        // Player (Diver)
        this.player = document.createElement('div');
        this.player.style.position = 'absolute';
        this.player.style.top = '20%';
        this.player.style.left = '50%';
        this.player.style.width = '40px';
        this.player.style.height = '60px';
        this.player.style.background = this.primaryColor;
        this.player.style.borderRadius = '20px 20px 10px 10px';
        this.player.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.player.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(this.player);

        this.playerX = this.container.clientWidth / 2;

        // Wind lines (visual speed effect)
        for (let i = 0; i < 5; i++) {
            this.createWindLine();
        }

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);

        // Spawn initial obstacles closer to the player for immediate action
        // Setup initial obstacles and guarantee comfortable reaction time at all speeds
        // Player is at ~80% from bottom. 
        const pHeightFromBottom = this.container.clientHeight * 0.8;

        // Give a balanced reaction time: 0.8s at speed x1, min 0.4s at max speed
        // so it doesn't exceed the total game duration!
        const sm = this.engine.speedMultiplier || 1.0;
        const speedFactor = 1 + (sm - 1) * 0.5;
        const timeToFirstImpact = Math.max(0.4, 0.8 / speedFactor);

        let startY1 = pHeightFromBottom - (this.obstacleSpeed * timeToFirstImpact);
        // Ensure it doesn't spawn ON TOP of the player, but can spawn on-screen if slow
        startY1 = Math.min(pHeightFromBottom - 100, startY1);
        this.spawnObstacle(startY1, true);

        // Queue subsequent obstacles
        let startY2 = startY1 - (this.obstacleSpeed * this.spawnInterval);
        this.spawnObstacle(startY2);

        let startY3 = startY2 - (this.obstacleSpeed * this.spawnInterval);
        this.spawnObstacle(startY3);
    }

    createWindLine() {
        const line = document.createElement('div');
        line.style.position = 'absolute';
        line.style.width = '2px';
        line.style.height = `${Math.random() * 50 + 20}px`;
        line.style.background = 'rgba(255, 255, 255, 0.3)';
        line.style.left = `${Math.random() * 100}%`;
        line.style.top = `${Math.random() * 100}%`;
        this.container.appendChild(line);

        // Animate
        const duration = Math.random() * 500 + 200;
        line.animate([
            { transform: 'translateY(0)' },
            { transform: 'translateY(-100vh)' }
        ], {
            duration: duration,
            iterations: Infinity
        });
    }

    spawnObstacle(startY = -50, isFirst = false) {
        const obs = document.createElement('div');
        obs.style.position = 'absolute';

        let width = Math.random() * 100 + 50;
        let left;

        if (isFirst) {
            // Force the first obstacle out of the center perfectly safely
            const mid = this.container.clientWidth / 2;
            const safeZone = 40; // width of player + some buffer
            if (Math.random() > 0.5) {
                // Left
                left = Math.random() * (mid - safeZone - width);
            } else {
                // Right
                left = mid + safeZone + Math.random() * (this.container.clientWidth - mid - safeZone - width);
            }
            // Fallback safe clamp
            left = Math.max(0, Math.min(left || 0, this.container.clientWidth - width));
        } else {
            left = Math.random() * (this.container.clientWidth - width);
        }

        obs.style.width = `${width}px`;
        obs.style.height = '20px';
        obs.style.background = this.secondaryColor;
        obs.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        obs.style.left = `${left}px`;
        obs.style.bottom = `${startY}px`;
        this.container.appendChild(obs);

        this.obstacles.push({
            element: obs,
            y: startY
        });
    }

    onDown(e) {
        if (!this.active) return;
        this.isDragging = true;
        this.lastDragX = e.clientX;
    }

    onMove(e) {
        if (!this.active || !this.isDragging) return;
        const dx = e.clientX - this.lastDragX;
        this.lastDragX = e.clientX;

        const rect = this.container.getBoundingClientRect();
        let newX = this.playerX + dx;
        newX = Math.max(20, Math.min(newX, rect.width - 20));

        this.playerX = newX;
        this.player.style.left = `${newX}px`;
    }

    onUp() {
        this.isDragging = false;
        this.lastDragX = null;
    }

    update(dt) {
        this.spawnTimer += dt / 1000;
        if (this.spawnTimer > this.spawnInterval) {
            this.spawnTimer = 0;
            let lowestY = this.container.clientHeight;
            for (let obs of this.obstacles) {
                if (obs.y < lowestY) lowestY = obs.y;
            }
            const spacing = this.obstacleSpeed * this.spawnInterval;
            this.spawnObstacle(Math.min(-50, lowestY - spacing));
        }

        // Move obstacles UP (since we are falling down)
        for (let i = this.obstacles.length - 1; i >= 0; i--) {
            const obs = this.obstacles[i];
            obs.y += this.obstacleSpeed * (dt / 1000);
            obs.element.style.bottom = `${obs.y}px`;

            // Collision Check
            const pRect = this.player.getBoundingClientRect();
            const oRect = obs.element.getBoundingClientRect();

            if (
                pRect.left < oRect.right &&
                pRect.right > oRect.left &&
                pRect.top < oRect.bottom &&
                pRect.bottom > oRect.top
            ) {
                this.lose();
            }

            // Remove if off screen
            if (obs.y > this.container.clientHeight + 50) {
                obs.element.remove();
                this.obstacles.splice(i, 1);
            }
        }
    }

    // Win if time runs out (survived)
    onTimeout() {
        return true;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
